import { calculateFare, type FareBreakdown, type Place, type TripStatus, type VehicleType } from '@raahi/shared';
import { DEMO_DRIVERS, DEMO_FARE_CONFIG, DEMO_HISTORY } from './data.js';

export interface DemoTripDriver {
  id: string;
  name: string;
  rating: number;
  vehicle: { make: string; model: string; plate: string; color: string; type: VehicleType };
}

export interface DemoTrip {
  id: string;
  status: TripStatus;
  vehicleType: VehicleType;
  pickup: Place;
  drop: Place;
  distanceKm: number;
  durationMin: number;
  fare: FareBreakdown;
  paymentMethod: 'cash' | 'card';
  requestedAt: string;
  assignedAt: string | null;
  arrivedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  driver?: DemoTripDriver;
  events: Array<{ type: string; at: string }>;
}

type Listener<T = unknown> = (payload: T) => void;

interface TripEnvelope {
  trip: DemoTrip;
  driverPosition: { lat: number; lng: number } | null;
  timers: ReturnType<typeof setTimeout>[];
  locationTimer: ReturnType<typeof setInterval> | null;
}

class DemoState {
  private active: TripEnvelope | null = null;

  private finished: DemoTrip[] = [];

  private listeners = new Map<string, Set<Listener>>();

  on(event: string, handler: Listener): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return () => set?.delete(handler);
  }

  private emit(event: string, payload: unknown): void {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }

  getActive(): DemoTrip | null {
    return this.active?.trip ?? null;
  }

  history(): DemoTrip[] {
    const seed: DemoTrip[] = DEMO_HISTORY.map((entry) => ({
      id: entry.id,
      status: entry.status,
      vehicleType: entry.vehicleType,
      pickup: entry.pickup,
      drop: entry.drop,
      distanceKm: entry.distanceKm,
      durationMin: entry.durationMin,
      fare: entry.fare,
      paymentMethod: entry.paymentMethod,
      requestedAt: entry.requestedAt,
      assignedAt: null,
      arrivedAt: null,
      startedAt: null,
      endedAt: entry.endedAt,
      events: [{ type: 'completed', at: entry.endedAt }],
    }));
    return [...this.finished, ...seed].slice(0, 40);
  }

  trip(id: string): DemoTrip | null {
    if (this.active?.trip.id === id) return this.active.trip;
    return this.finished.find((t) => t.id === id) ?? null;
  }

  request(input: { vehicleType: VehicleType; pickup: Place; drop: Place; paymentMethod: 'cash' | 'card' }): DemoTrip {
    if (this.active) throw new Error('You already have a ride in progress.');

    const config = DEMO_FARE_CONFIG[input.vehicleType];
    const distanceKm = haversine(input.pickup, input.drop);
    const durationMin = Math.max(1, Math.round((distanceKm / 28) * 60));
    const fare = calculateFare(config, distanceKm, durationMin, 1);

    const now = new Date().toISOString();
    const trip: DemoTrip = {
      id: `demo-trip-${Date.now()}`,
      status: 'requested',
      vehicleType: input.vehicleType,
      pickup: input.pickup,
      drop: input.drop,
      distanceKm,
      durationMin,
      fare,
      paymentMethod: input.paymentMethod,
      requestedAt: now,
      assignedAt: null,
      arrivedAt: null,
      startedAt: null,
      endedAt: null,
      events: [{ type: 'requested', at: now }],
    };

    this.active = { trip, driverPosition: null, timers: [], locationTimer: null };
    this.scheduleSimulation();
    return trip;
  }

  cancel(tripId: string): DemoTrip | null {
    if (!this.active || this.active.trip.id !== tripId) return null;
    if (this.active.trip.status === 'in_progress' || this.active.trip.status === 'completed') {
      throw new Error('You cannot cancel a trip that has already started.');
    }
    this.advance('cancelled', { endedAt: new Date().toISOString() });
    return this.archiveActive();
  }

  rate(tripId: string): DemoTrip | null {
    const trip = this.trip(tripId);
    if (!trip) return null;
    trip.events.push({ type: 'rider_rated', at: new Date().toISOString() });
    return trip;
  }

  private scheduleSimulation(): void {
    if (!this.active) return;
    const env = this.active;

    const queue: Array<{ delay: number; run: () => void }> = [
      { delay: 700, run: () => this.advance('searching') },
      {
        delay: 1800,
        run: () => {
          const pickedDriver = pickNearestDriver(env.trip.pickup, env.trip.vehicleType);
          env.driverPosition = { ...pickedDriver.position };
          this.advance('assigned', {
            assignedAt: new Date().toISOString(),
            driver: {
              id: pickedDriver.id,
              name: pickedDriver.name,
              rating: pickedDriver.rating,
              vehicle: pickedDriver.vehicle,
            },
          });
        },
      },
      { delay: 400, run: () => this.advance('arriving') },
      { delay: 6000, run: () => this.advance('arrived', { arrivedAt: new Date().toISOString() }) },
      { delay: 2200, run: () => this.advance('in_progress', { startedAt: new Date().toISOString() }) },
      {
        delay: 9000,
        run: () => {
          this.advance('completed', { endedAt: new Date().toISOString() });
          this.archiveActive();
        },
      },
    ];

    let elapsed = 0;
    for (const step of queue) {
      elapsed += step.delay;
      env.timers.push(setTimeout(step.run, elapsed));
    }

    env.locationTimer = setInterval(() => this.tickDriverPosition(), 1100);
  }

  private tickDriverPosition(): void {
    const env = this.active;
    if (!env || !env.driverPosition) return;
    const { trip } = env;
    if (trip.status !== 'arriving' && trip.status !== 'in_progress' && trip.status !== 'assigned') return;

    const target = trip.status === 'in_progress' ? trip.drop : trip.pickup;
    const next = stepToward(env.driverPosition, target, trip.status === 'in_progress' ? 0.0035 : 0.0025);
    env.driverPosition = next;
    this.emit('driver:location', { position: next, heading: 0, at: new Date().toISOString() });
  }

  private advance(to: TripStatus, patch: Partial<DemoTrip> = {}): void {
    if (!this.active) return;
    const next: DemoTrip = {
      ...this.active.trip,
      ...patch,
      status: to,
      events: [...this.active.trip.events, { type: to, at: new Date().toISOString() }],
    };
    this.active.trip = next;
    this.emit('trip:update', { tripId: next.id, status: next.status, at: new Date().toISOString(), driver: next.driver });
    if (to === 'completed') {
      this.emit('trip:fare', { tripId: next.id, fare: next.fare });
    }
  }

  private archiveActive(): DemoTrip | null {
    if (!this.active) return null;
    const finished = this.active.trip;
    for (const timer of this.active.timers) clearTimeout(timer);
    if (this.active.locationTimer) clearInterval(this.active.locationTimer);
    this.finished = [finished, ...this.finished].slice(0, 20);
    this.active = null;
    return finished;
  }
}

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return Number((2 * R * Math.asin(Math.sqrt(h))).toFixed(2));
}

function pickNearestDriver(pickup: { lat: number; lng: number }, type: VehicleType) {
  const candidates = DEMO_DRIVERS.filter((d) => d.vehicle.type === type);
  if (candidates.length === 0) return DEMO_DRIVERS[0]!;
  return candidates
    .map((d) => ({ d, dist: haversine(pickup, d.position) }))
    .sort((a, b) => a.dist - b.dist)[0]!.d;
}

function stepToward(from: { lat: number; lng: number }, to: { lat: number; lng: number }, step: number): { lat: number; lng: number } {
  const dLat = to.lat - from.lat;
  const dLng = to.lng - from.lng;
  const dist = Math.hypot(dLat, dLng);
  if (dist <= step) return { lat: to.lat, lng: to.lng };
  const ratio = step / dist;
  return { lat: from.lat + dLat * ratio, lng: from.lng + dLng * ratio };
}

export const demoState = new DemoState();
